/**
 * @xivdyetools/core - Core-Specific Type Definitions
 *
 * Color matching algorithm types defined by core.
 * For shared types (Dye, RGB, etc.), import from '@xivdyetools/types'.
 *
 * @module types
 */
/** Suite display order: ΔE2000 · ΔEOK2 · ΔE76 · REDMEAN · RGB DIST · DISTINGUISH % */
export const MATCHING_METHODS = [
    'ciede2000',
    'oklab',
    'cie76',
    'redmean',
    'rgb',
    'distinguish',
];
/** ΔE2000 is the default everywhere — one answer to "what does CLOSE mean". */
export const DEFAULT_MATCHING_METHOD = 'ciede2000';
/**
 * Method display tags (the short-key vocabulary; `ratio` included for the
 * two tools that print it). Tags are identifiers — never localise them.
 */
export const MATCHING_METHOD_TAGS = {
    ciede2000: 'ΔE2000',
    oklab: 'ΔEOK2',
    cie76: 'ΔE76',
    redmean: 'REDMEAN',
    rgb: 'RGB DIST',
    distinguish: 'DISTINGUISH %',
    ratio: 'RATIO',
};
/**
 * Retired v4 stored values → their 5.0 replacement. Both retired methods
 * fold into the suite default (there is no perceptual successor that keeps
 * their exact behaviour, and a silently different non-default would be
 * worse than the default).
 */
export const LEGACY_MATCHING_METHOD_MAP = {
    hyab: 'ciede2000',
    'oklch-weighted': 'ciede2000',
    // Pre-5.0 deep links used 'euclidean' informally for RGB distance
    euclidean: 'rgb',
};
/** Type guard for the 5.0 vocabulary. */
export function isMatchingMethod(value) {
    return typeof value === 'string' && MATCHING_METHODS.includes(value);
}
/**
 * Normalize any stored/parsed method value (KV preference, localStorage,
 * URL `algo` param, API body) into the 5.0 vocabulary: current values pass
 * through, retired values map, anything else falls back to the default.
 */
/**
 * BUG-011: `value in OBJ` and `OBJ[value]` both walk `Object.prototype`, so
 * `'constructor'`, `'toString'` and `'__proto__'` looked like legacy method
 * names and returned a FUNCTION typed as a `MatchingMethod`. This normalizer is
 * applied at every ingress — KV preference, localStorage, the URL `algo` param,
 * API bodies — and its whole contract is "anything else falls back to the
 * default", so a crafted value propagated a function into the matching method
 * and `getDistanceForMethod`'s exhaustive switch then returned `undefined`.
 *
 * `Object.hasOwn` is the fix rather than a `Map` because
 * `LEGACY_MATCHING_METHOD_MAP` is a published export; changing its type would
 * be a breaking change for a defect that needs a guard, not a new shape.
 */
export function normalizeMatchingMethod(value) {
    if (isMatchingMethod(value))
        return value;
    if (typeof value === 'string' && Object.hasOwn(LEGACY_MATCHING_METHOD_MAP, value)) {
        return LEGACY_MATCHING_METHOD_MAP[value];
    }
    return DEFAULT_MATCHING_METHOD;
}
//# sourceMappingURL=index.js.map